import { useEffect, useState } from "react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { IcoInput } from "@/components/shared/IcoInput";
import { NumInput } from "@/components/shared/NumInput";
import { DocCurrency } from "@/components/shared/DocCurrency";
import { CreditNoteFields } from "@/components/shared/CreditNoteFields";
import { formatDate } from "@/lib/format";
import { Building2, Check, FileText, PencilLine, UserRound } from "lucide-react";

// 371 (varianta B): Údaje dokladu jako dvě karty — Dodavatel (u vydané Odběratel) a Doklad.
// Uložený doklad ukazuje hodnoty, tužka kartu přepne do polí (✓ zpět); nový doklad (sken,
// ruční zadání) i doklad bez protistrany má karty rovnou otevřené. Ukládá se dál „Uložit změny“.
const inp = "h-11 md:h-10 text-base md:text-sm w-full min-w-0";

function Field({ label, children, className = "" }) {
  return <div className={`min-w-0 ${className}`}><Label className="text-[13px] text-muted-foreground font-medium">{label}</Label><div className="mt-1">{children}</div></div>;
}

function Card({ title, icon: Icon, editing, onToggle, testid, toggleTestid, children }) {
  return (
    <div className="rounded-[18px] border border-border bg-card p-3.5 md:p-4 space-y-3 min-w-0" data-testid={testid}>
      <div className="flex items-center gap-2 text-[13px] font-semibold text-muted-foreground">
        <Icon size={15} className="shrink-0" /><span className="flex-1 min-w-0 truncate">{title}</span>
        {onToggle && (
          <button type="button" onClick={onToggle} aria-pressed={editing} aria-label={editing ? "Hotovo" : `Upravit: ${title}`} title={editing ? "Hotovo" : "Upravit"}
            data-testid={toggleTestid}
            className={`w-8 h-8 -my-1 -mr-1 rounded-full grid place-items-center shrink-0 ${editing ? "bg-foreground text-background" : "text-muted-foreground hover:text-foreground hover:bg-secondary"}`}>
            {editing ? <Check size={16} /> : <PencilLine size={15} />}
          </button>
        )}
      </div>
      {children}
    </div>
  );
}

function Tile({ label, value, danger }) {
  return (
    <div className="rounded-[12px] bg-secondary/50 px-3 py-2 min-w-0">
      <div className="text-xs text-muted-foreground truncate">{label}</div>
      <div className={`font-semibold tabular-nums truncate ${danger ? "text-destructive" : ""}`} title={value || ""}>{value || "—"}</div>
    </div>
  );
}

// partyLabel — „Dodavatel“ / „Odběratel“ (u vydané s poznámkou „· z investora zakázky“)
export function DocPartyCards({ doc, setDoc, set, setThem, issued, partyLabel, fromAres, overdue }) {
  const party = issued ? "Odběratel" : "Dodavatel";
  const [editParty, setEditParty] = useState(false);
  const [editHead, setEditHead] = useState(false);
  useEffect(() => {
    setEditParty(!doc.id || !(doc.supplier_name || "").trim());
    setEditHead(!doc.id);
  }, [doc.id]); // eslint-disable-line react-hooks/exhaustive-deps
  const n = Number(String(doc.total_without_vat ?? "").replace(",", ".")) || 0;
  const v = Number(String(doc.vat_amount ?? "").replace(",", ".")) || 0;
  const g = Number(String(doc.total_with_vat ?? "").replace(",", ".")) || 0;
  const ids = [doc.supplier_ico && `IČO ${doc.supplier_ico}`, doc.supplier_dic && `DIČ ${doc.supplier_dic}`].filter(Boolean).join(" · ");
  const letter = (doc.supplier_name || "?").trim().charAt(0).toUpperCase() || "?";
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-2.5 items-start">
      <Card title={party} icon={issued || !doc.supplier_ico ? UserRound : Building2} editing={editParty} testid="doc-party-card" toggleTestid="doc-party-edit"
        onToggle={doc.id ? () => setEditParty((e) => !e) : null}>
        {editParty ? (
          <div className="space-y-2.5">
            <Field label="IČO"><IcoInput key={doc.id || "new"} value={doc.supplier_ico} onChange={(x) => setThem("supplier_ico", x)} onFound={fromAres} className={inp} testid="doc-ico" /></Field>
            <Field label={partyLabel}><Input data-testid="doc-supplier" value={doc.supplier_name || ""} onChange={(e) => setThem("supplier_name", e.target.value)} className={inp} /></Field>
            <Field label="DIČ"><Input value={doc.supplier_dic || ""} onChange={(e) => setThem("supplier_dic", e.target.value)} className={inp} data-testid="doc-dic" /></Field>
          </div>
        ) : (
          <div className="flex items-center gap-3 min-w-0">
            <span className="w-10 h-10 rounded-[12px] bg-secondary grid place-items-center font-heading font-extrabold shrink-0" aria-hidden>{letter}</span>
            <div className="min-w-0">
              <div className="font-bold truncate" data-testid="doc-party-card-name">{doc.supplier_name || `${party} neuveden`}</div>
              <div className="text-[13px] text-muted-foreground truncate">{ids || (issued ? "soukromá osoba" : "bez IČO")}</div>
            </div>
          </div>
        )}
      </Card>

      <Card title="Doklad" icon={FileText} editing={editHead} testid="doc-head-card" toggleTestid="doc-head-edit"
        onToggle={doc.id ? () => setEditHead((e) => !e) : null}>
        {editHead ? (
          <>
            <div className="grid grid-cols-2 gap-2.5">
              <Field label="Číslo dokladu"><Input value={doc.invoice_number || ""} onChange={(e) => set("invoice_number", e.target.value)} className={inp} data-testid="doc-number" /></Field>
              <Field label="Variabilní symbol"><Input value={doc.variable_symbol || ""} onChange={(e) => set("variable_symbol", e.target.value)} className={inp} /></Field>
              <Field label="Vystaveno"><Input type="date" value={(doc.issue_date || "").slice(0, 10)} onChange={(e) => set("issue_date", e.target.value)} className={inp} /></Field>
              <Field label="Splatnost"><Input type="date" value={(doc.due_date || "").slice(0, 10)} onChange={(e) => set("due_date", e.target.value)} className={inp} /></Field>
            </div>
            <div className="grid grid-cols-2 gap-2.5">
              <Field label="Bez DPH"><NumInput value={doc.total_without_vat} onChange={(x) => set("total_without_vat", x)} className={`${inp} text-right tabular-nums`} /></Field>
              <Field label="DPH"><NumInput value={doc.vat_amount} onChange={(x) => set("vat_amount", x)} className={`${inp} text-right tabular-nums`} /></Field>
              <Field label="Celkem s DPH" className="col-span-2"><NumInput data-testid="doc-total" value={doc.total_with_vat} onChange={(x) => set("total_with_vat", x)} className={`${inp} text-right tabular-nums font-semibold`} /></Field>
            </div>
          </>
        ) : (
          <div className="grid grid-cols-2 gap-2">
            <Tile label="Číslo dokladu" value={doc.invoice_number} />
            <Tile label="Variabilní symbol" value={doc.variable_symbol} />
            <Tile label="Vystaveno" value={doc.issue_date ? formatDate(doc.issue_date) : ""} />
            <Tile label="Splatnost" value={doc.due_date ? formatDate(doc.due_date) : ""} danger={overdue} />
          </div>
        )}
        {n > 0 && g > 0 && Math.abs(n + v - g) > 1 && (
          <div className="flex items-center justify-between gap-2 text-xs text-amber-600 dark:text-amber-400" data-testid="doc-vat-mismatch">
            <span>Bez DPH + DPH ≠ Celkem</span>
            <button type="button" className="font-bold underline hover:no-underline" data-testid="doc-vat-fill" onClick={() => set("vat_amount", Math.round((g - n) * 100) / 100)}>Dopočítat</button>
          </div>
        )}
        <DocCurrency doc={doc} setDoc={setDoc} />
        {doc.is_credit_note && <CreditNoteFields doc={doc} setDoc={setDoc} issued={issued} />}
      </Card>
    </div>
  );
}

// 372 (varianta B): účtenka — karty Obchod a Doklad. Stav úprav drží detail účtenky (uložení s hlavičkou).
export function ReceiptCards({ rec, set, setRec, isNew, editShop, setEditShop, editHead, setEditHead }) {
  const shopOpen = isNew || editShop, headOpen = isNew || editHead;
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-2.5 items-start">
      <Card title="Obchod" icon={Building2} editing={shopOpen} testid="receipt-shop-card" toggleTestid="receipt-shop-edit"
        onToggle={isNew ? null : () => setEditShop((e) => !e)}>
        {shopOpen ? (
          <div className="space-y-2.5">
            <Field label="Obchod"><Input value={rec.supplier_name || ""} onChange={(e) => set("supplier_name", e.target.value)} className={inp} data-testid="receipt-supplier-input" /></Field>
            <Field label="Adresa"><Input value={rec.address || ""} onChange={(e) => set("address", e.target.value)} className={inp} /></Field>
          </div>
        ) : (
          <div className="min-w-0">
            <div className="font-bold truncate">{rec.supplier_name || "Obchod neuveden"}</div>
            <div className="text-[13px] text-muted-foreground truncate">{rec.address || "bez adresy"}</div>
          </div>
        )}
      </Card>
      <Card title="Doklad" icon={FileText} editing={headOpen} testid="receipt-head-card" toggleTestid="receipt-head-edit"
        onToggle={isNew ? null : () => setEditHead((e) => !e)}>
        {headOpen ? (
          <div className="grid grid-cols-2 gap-2.5">
            <Field label="Datum"><Input type="date" value={(rec.date || "").slice(0, 10)} onChange={(e) => set("date", e.target.value)} className={inp} data-testid="receipt-date-input" /></Field>
            <Field label="Číslo dokladu"><Input value={rec.receipt_number || ""} onChange={(e) => set("receipt_number", e.target.value)} className={inp} placeholder="č. účtenky" data-testid="receipt-number" /></Field>
            <Field label="Celkem s DPH"><Input type="number" value={rec.total} onChange={(e) => set("total", e.target.value)} className={`${inp} text-right tabular-nums font-semibold`} data-testid="receipt-total-input" /></Field>
            <Field label="DPH"><Input type="number" value={rec.vat_amount} onChange={(e) => set("vat_amount", e.target.value)} className={`${inp} text-right tabular-nums`} /></Field>
          </div>
        ) : (
          <div className="grid grid-cols-2 gap-2">
            <Tile label="Datum" value={rec.date ? formatDate(rec.date) : ""} />
            <Tile label="Číslo dokladu" value={rec.receipt_number} />
          </div>
        )}
        {(headOpen || rec.foreign) && <DocCurrency doc={rec} setDoc={setRec} totalKey="total" dateKey="date" kind="Účtenka" />}
      </Card>
    </div>
  );
}
